import React, { forwardRef } from "react";
import { EntityCard, EntityCardRef, EntityCardProps, BaseEntity } from "components/Entities/EntityCardBase";
import { Chip } from "ui/Chip";
import { Feedback } from "util/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/InputSelect";
import { useUser } from "util/UserContext";
import PostKindChip from "components/Posts/display/PostKindChip";

interface FeedbackCardRefactoredProps extends Omit<EntityCardProps<Feedback>, "entity" | "entityType" | "renderTags" | "renderAdditionalContent" | "renderEditFields" | "renderActions" | "renderPrimaryContent"> {
  feedback: Feedback;
  onTitleChange?: (value: string) => void;
  onCategoryChange?: (value: Feedback['properties']['category']) => void;
  onPrivacyChange?: (value: string) => void;
  onStatusChange?: (value: Feedback['status']) => void;
  titleValidationMessage?: string;
  descriptionValidationMessage?: string;
}

/**
 * Refactored FeedbackCard using the generic EntityCard component
 */
const FeedbackCardRefactored = forwardRef<EntityCardRef, FeedbackCardRefactoredProps>(
  (props, ref) => {
    const { 
      feedback, 
      onTitleChange,
      onCategoryChange,
      onPrivacyChange,
      onStatusChange,
      titleValidationMessage,
      editMode = false,
      ...restProps 
    } = props;
    
    const { currentUser } = useUser();
    
    // Helper function to check if title should be hidden
    const shouldHideTitle = (title?: string): boolean => {
      return !title || title.trim() === '' || title.trim() === '[no_title]';
    };
    
    // Helper function to get chip color for status
    const getChipColor = (status: Feedback['status']) => {
      switch (status) {
        case 'Open':
          return '#cf1f1f';
        case 'Reviewing':
          return '#005286';
        case 'Planned':
          return '#df7023';
        case 'Unplanned':
          return '#cf1f1f';
        case 'Developing':
          return '#8e00a7';
        case 'Preview':
          return '#4e00a7';
        case 'Released':
          return '#419357';
        case 'Closed':
          return '#666666';
        default:
          return '#555555';
      }
    };

    // Render feedback-specific tags
    const renderTags = (entity: BaseEntity) => {
      const feedback = entity as Feedback;
      
      return (
        <>
          <PostKindChip kind="feedback" />
          
          {editMode ? (
            <>
              <Select
                onValueChange={onCategoryChange}
                value={feedback.properties?.category}
              >
                <SelectTrigger className="entity-filter-select" aria-label="Feedback category">
                  <SelectValue placeholder="Category (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bug">Bug</SelectItem>
                  <SelectItem value="Enhancement">Enhancement</SelectItem>
                  <SelectItem value="Functionality">Functionality</SelectItem>
                  <SelectItem value="User Interface (UI)">User Interface (UI)</SelectItem>
                  <SelectItem value="User Experience (UX)">User Experience (UX)</SelectItem>
                  <SelectItem value="Performance">Performance</SelectItem>
                  <SelectItem value="Compliance">Compliance</SelectItem>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              
              {currentUser?.is_admin && (
                <Select 
                  onValueChange={onStatusChange} 
                  value={feedback.status}
                  required
                  aria-required="true"
                >
                  <SelectTrigger className="entity-filter-select">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Reviewing">Reviewing</SelectItem>
                    <SelectItem value="Planned">Planned</SelectItem>
                    <SelectItem value="Unplanned">Unplanned</SelectItem>
                    <SelectItem value="Developing">Developing</SelectItem>
                    <SelectItem value="Preview">Preview</SelectItem>
                    <SelectItem value="Released">Released</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </>
          ) : (
            <>
              {feedback.properties?.category && (
                <Chip
                  variant="custom"
                  size="sm"
                  className="bg-gray-600 text-white"
                >
                  {feedback.properties.category}
                </Chip>
              )}
              {feedback.status && (
                <Chip
                  variant="custom"
                  size="sm"
                  className="text-white"
                  style={{ backgroundColor: getChipColor(feedback.status) }}
                >
                  {feedback.status}
                </Chip>
              )}
            </>
          )}
        </>
      );
    };

    // Remove custom edit fields to avoid duplication; use EntityCard's built-in editors
    const renderEditFields = (_entity: BaseEntity) => null;

    // No special actions for feedback (no thumbnails or embeds)
    const renderActions = () => null;
    
    // No additional content for feedback
    const renderAdditionalContent = () => null;

    return (
        <EntityCard
        ref={ref}
        entity={feedback}
        entityType="feedback"
        renderTags={renderTags}
        renderAdditionalContent={renderAdditionalContent}
        renderEditFields={renderEditFields}
        renderActions={renderActions}
        hideTitle={shouldHideTitle(feedback.title)}
        descriptionPlaceholder="Body"
          canEditPrivacy={true}
          canEditTitle={true}
        editMode={editMode}
          onTitleChange={onTitleChange}
          titleValidationMessage={titleValidationMessage}
        onPrivacyChange={onPrivacyChange}
        {...restProps}
      />
    );
  }
);

FeedbackCardRefactored.displayName = "FeedbackCardRefactored";

export default FeedbackCardRefactored;
